import React, { FC, useLayoutEffect } from "react";
import { useTabState } from "reakit/Tab";
import { TabsContext, TabsContextShape } from "./TabsContext";

export interface TabsProps {
    /**
     * Callback when the selected tab is requested to be changed via some DOM event: click, arrow, focus, etc.
     */
    onChange: (newSelectedId: string) => void;

    /**
     * The id of the selected tab.
     */
    selectedId: string;

    /**
     * Whether the tab selection should be manual.
     * - true will not automatically show the respective panel for the focused tab. It will require
     *   enter/space/click to show.
     *
     * Typically, manual activation of tabs is only necessary when panels cannot be displayed
     * instantly, i.e., not all the panel content is present in the DOM.
     *
     * See the following link for more information:
     * https://github.com/w3c/aria-practices/commit/1653cef371a3c51702a454ff0134b68f128f756b#diff-9f2d77b171149e2aeae0b51f30cca0f92f6c12c8eb09aa74dc5f8b528fb00869R2446
     *
     * Please chat with your friendly neighborhood UX designer before making this decision.
     */
    manual?: boolean;

    /**
     * If true, the `TabPanel`s in this `Tabs` component will only render their children when that `TabPanel`'s respective tab is active.
     * This can be a helpful performance optimization if the children of hidden tabs are complex.
     */
    onlyRenderActiveTabPanelChildren?: boolean;
}

export const Tabs: FC<TabsProps> = ({
    children,
    manual = false,
    onlyRenderActiveTabPanelChildren = false,
    selectedId,
    onChange,
}) => {
    const tab = useTabState({
        // Note: these are initial props, they are not used in on the 2nd+ render.
        // Need to re-sync them if they change via setters on the return value from useTabState.
        // Note: no setter for `manual`. This is unfortunate.
        // TODO maybe we can just manually set it into `tab`??
        manual,
        selectedId,
    });

    // Re-sync the selectedId to Reakit's tab state.
    const tabSelectedId = tab.selectedId;
    const tabSelect = tab.select;
    useLayoutEffect(() => {
        if (selectedId !== tabSelectedId) {
            tabSelect(selectedId);
        }
    }, [selectedId, tabSelectedId, tabSelect]);

    // Using Object.values to do a shallow comparison of the object on purpose.
    const value = React.useMemo<TabsContextShape>(
        () => ({
            onChange,
            onlyRenderActiveTabPanelChildren,
            ...tab,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onChange, onlyRenderActiveTabPanelChildren, ...Object.values(tab)]
    );

    return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
};
